import { Observable } from 'rxjs/Observable';
import { EventSource } from './eventSource';
import { ProcessedAclRole } from './interfaces';

import { PageSettings, PageSetting, observePageSettings$ } from '../eventDataSourceUpgrades/observePageSettings';
import { observeProcessedEventAclRole$ } from '../eventDataSourceUpgrades/observeProcessedEventAclRole';

import 'rxjs/add/observable/combineLatest'
import 'rxjs/add/observable/of'
import 'rxjs/add/operator/distinctUntilChanged'
import 'rxjs/add/operator/map'
import 'rxjs/add/operator/pluck'

export interface PublicInfo {
    customPallets: Object;
    date: number;
    fiancee: string;
    message: string;
    myPalette: Object;
    myStyle: Object;
    owner: string;
    pageOrder: Object;
    place: string;
}

export interface InfoSecuritySettings {
    info: {
        showDate: boolean;
        showPlace: boolean;
    };
    users: {
        hideCountdownTimer: boolean;
        hideGuestList: boolean;
        hideDownloadAppPage: boolean;
        lockedRSVP: boolean;
        siteUsesCode: boolean;
    }
}

export type Page = string;

export class EventInfo {

    private es: EventSource;

    constructor(eventSource: EventSource) {
        this.es = eventSource;
    }

    public pluckPublicInfo(key: Array<string>) {
        return this.es.observePublicInfo().pluck(...key).map(res => JSON.stringify(res)).distinctUntilChanged().map(json => json ? JSON.parse(json) : {});
    }

    public fetchPlublicDateInfo() {
        const api = this.es.getDatasource().getEvent().getBestServiceApi();
        return Observable.fromPromise(
            api.queryGraphQLWithErrors(`
            query {
                event(eventId:"${this.es.getDatasource().getEvent().getEventId()}") {
                    info {
                        date
                        finalizedDate
                        tentativeDate
                        tzid
                    }
                }
            }
            `)
            .then(data => data.event.info)
        );
    }

    /**
     * This looks weird but it helps to normalize the data coming out of firebase, which is even weirder...
     */
    public infoSecuritySettings(): Observable<InfoSecuritySettings> {
        return Observable.combineLatest(observeProcessedEventAclRole$(this.es), this.es.observeSecurity()).map(res => {
            const role = res[0];
            const security = res[1];
            return Object.keys(security).reduce((acc, key) => {
                if (key === 'users') {
                    return { ...acc, [key]: Object.keys(security[key]).reduce((userAcc, userCurr) => {
                        if (userCurr === 'lockedRSVP') {
                            return { ...userAcc, lockedRSVP: role.isCrasher ? false : true };
                        }
                        if (userCurr === 'passwordFree') {
                            return { ...userAcc, siteUsesCode: security.users.passwordFree === undefined };
                        } else {
                            return { ...userAcc, [userCurr]: security[key][userCurr] !== undefined ? security[key][userCurr] : false  };
                        }
                    }, {
                        hideCountdownTimer: false,
                        hideGuestList: false,
                        hideDownloadAppPage: false,
                        lockedRSVP: false,
                        siteUsesCode: true
                    }) };
                } else if (key === 'info') {
                    return { ...acc, [key]: Object.keys(security[key]).reduce((infoAcc, infoCurr) => {
                        const value = security[key][infoCurr] !== undefined ? !!security[key][infoCurr].read : true;
                        if (infoCurr === 'place') {
                            return { ...infoAcc, showPlace: role.isCrasher ? value: true };
                        } else if (infoCurr === 'date') {
                            return { ...infoAcc, showDate: role.isCrasher ? value: true };
                        } else {
                            return infoAcc;
                        }
                    }, {
                        showDate: true,
                        showPlace: true
                    }) }
                } else {
                    return acc;
                }
            },{
                info: {
                    showDate: true,
                    showPlace: true
                },
                users: {
                    hideCountdownTimer: false,
                    hideGuestList: false,
                    hideDownloadAppPage: false,
                    lockedRSVP: false,
                    siteUsesCode: true
                }
            })
        })
    };

    public pageListDisplay() {
        const overridePageSlug = (page) => {
            if (page === 'vip') {
                return 'weddingparty';
            } else if (page === 'faq') {
                return 'qa';
            } else if (page === 'registry') {
                return 'registrylinks';
            }
            else {
                return page;
            }
        }
        const overridePageString = (page, pageStrings): string => {
            if (pageStrings[page]) {
                return pageStrings[page];
            } else if (page === 'vip') {
                return 'Wedding Party';
            } else if (page === 'faq') {
                return 'Q & A';
            } else {
                return page.charAt(0).toUpperCase() + page.slice(1);
            }
        }
        return Observable.combineLatest(this.availablePages(), this.pageStringOverride(), (pages, pageStrings) => {
            return pages.map((page) => {
                return {
                    name: overridePageString(page, pageStrings),
                    url: overridePageSlug(page)
                }
            })
        })
    }

    public publicPageListDisplay() {
        return Observable.combineLatest(this.pageListDisplay(), this.availablePublicPages(), (pages, security) => {
            return pages.filter(p => {
                return (security.indexOf(Object.keys(p)[0]) > -1) ? p : null;
            })
        })
    }

    private availablePublicPages() {
        return Observable.combineLatest(this.pageSecurity(), this.pageOrder(), (sec: Array<any> = [], page:Array<any> = []) => page.filter(p => (sec.indexOf(p) > -1) ? p : null))
    }

    private availablePages(): Observable<Array<Page>> {
        function _isUnlocked(pageSetting?: PageSetting) {
            return (pageSetting && pageSetting.unlocked) || false;
        }
        return Observable.combineLatest(
            observePageSettings$(this.es),
            this.pageOrder().switchMap(pages => pages.length > 0 ? this.pageOrder() : this.pageNoOrder()),
            this.visibleAuxPages(),
            (pageSettings: PageSettings = {}, pages: Array<Page> = [], auxPages: Array<Page>) => {
                const standardPages = pages.filter(p => {
                    if (p === "guests") {
                        // Guests Page is (Mobile) App Only
                        return null;
                    }
                    pageSettings = pageSettings || {};
                    const settingsForPage = <PageSetting>pageSettings[p];
                    if (! settingsForPage) {
                        return null;
                    }

                    if (p === "RSVP") {
                        // the RSVP Page is where we identify you as a Guest;
                        //   we cannot trust `unlocked` because of chicken-before-egg
                        return (settingsForPage.isPresentable ? p : null);
                    }
                    if ((p === "story") || (p === "tidbits")) {
                        // Story + Tidbits are presented as a unified Page;
                        //   *both* must be locked for it to not appear
                        const eitherUnlocked = (_isUnlocked(pageSettings.story) || _isUnlocked(pageSettings.tidbits));
                        return (eitherUnlocked ? p : null);
                    }
                    return (_isUnlocked(settingsForPage) ? p : null);
                });
                return standardPages.concat(auxPages);
            }
        );
    }

    private pageOrder() {
        return this.pluckPublicInfo(['pageOrder']).map(data => Object.keys(data).sort((a, b) => parseInt(data[a]) - parseInt(data[b])))
    }

    private pageNoOrder() {
        return Observable.of([
            'welcome',
            'story',
            'schedule',
            'travel',
            'faq',
            'vip',
            'guests',
            'registry',
            'moments',
            'RSVP'
        ]);
    }

    private pageStringOverride(): Observable<{[page: string]: string}> {
        return Observable.combineLatest(
            this.pluckPublicInfo(['stringOverrides']),
            this.auxPageStringOverride(),
            (overrides, auxOverrides) => {
                return {
                    ...overrides,
                    ...auxOverrides
                }
            }
        );
    }

    private pageSecurity() {
        return this.es.pluckSecurity(['pages']).map(pages => Object.keys(pages).filter((key: any) => (pages[key].read) ? key : null));
    }

    private channelSecurity() {
        return this.es.pluckSecurity(['channels']).map(channels => Object.keys(channels).filter((key: any) => (channels[key].read) ? key : null));
    }

    // an aux page is a page that exists in the client but is not part of the pages schema
    private auxPages(): Observable<Array<Page>> {
        return Observable.create((observer) => {
            observer.next([
                'app'
            ])
        })
    }

    private auxPageVisibility(): Observable<{[page: string]: boolean}> {
        return Observable.combineLatest(this.channelSecurity(), this.infoSecuritySettings()).map(securitySettings => {
            const settings = securitySettings[1];
            return {
                app: settings && settings.users ? ! settings.users.hideDownloadAppPage : true
            }
        })
    }

    private auxPageStringOverride(): Observable<{[page: string]: string}> {
        return Observable.create(observer => observer.next({
            app: 'Get Our App'
        }))
    }

    private visibleAuxPages(): Observable<Array<Page>> {
        return Observable.combineLatest(
            this.auxPages(),
            this.auxPageVisibility(), (auxPages: Array<Page>, vis: {[page: string]: boolean}) => {
                return auxPages.filter(p => vis[p]);
        });
    }
}
